export type CondominiumDateObject = {
  day: number
  month: number
  year: number
}

export function isCondominiumEndingAtSet(value: unknown): boolean {
  if (value === null || value === undefined || value === '' || value === -1 || value === '-1') {
    return false
  }
  if (typeof value === 'object') {
    const dateObject = value as Partial<CondominiumDateObject>
    if (Object.keys(dateObject).length === 0) {
      return false
    }
    if (dateObject.year === undefined || dateObject.year === -1 || dateObject.year <= 0) {
      return false
    }
  }
  return true
}

export function dateObjectToComparableInput(value: unknown): string | null {
  if (!value || typeof value !== 'object') {
    return null
  }
  const dateObject = value as Partial<CondominiumDateObject>
  if (dateObject.year === undefined || dateObject.year === -1 || dateObject.year <= 0) {
    return null
  }
  return `${dateObject.year}-${String(dateObject.month).padStart(2, '0')}-${String(dateObject.day).padStart(2, '0')}`
}

export function dateObjectToMs(value: unknown): number | null {
  const input = dateObjectToComparableInput(value)
  if (!input) {
    return null
  }
  const ms = new Date(input).getTime()
  return Number.isFinite(ms) ? ms : null
}

export function computePreviousEndingAtFromNewStart(newStartingAt: unknown): CondominiumDateObject | null {
  const newStartMs = dateObjectToMs(newStartingAt)
  if (newStartMs === null) {
    return null
  }
  const previousDay = new Date(newStartMs - 86400000)
  return {
    day: previousDay.getDate(),
    month: previousDay.getMonth() + 1,
    year: previousDay.getFullYear()
  }
}

export type CondominiumOwnershipRow = {
  id?: string
  internalId?: string | number
  objectId?: string
  startingAt?: unknown
  endingAt?: unknown
  status?: string
  modified?: boolean
}

export function getLatestPersistedCondominiumByStart(
  condominiums: Array<{ id?: string; objectId?: string; startingAt?: unknown; endingAt?: unknown }>,
  objectId?: string
): CondominiumOwnershipRow | null {
  const persisted = objectId
    ? condominiums.filter(c => c.id && c.objectId === objectId)
    : condominiums.filter(c => c.id)
  if (persisted.length === 0) {
    return null
  }
  return persisted.sort((a, b) => (dateObjectToMs(b.startingAt) || 0) - (dateObjectToMs(a.startingAt) || 0))[0]
}

export function getPreviousCondominiumForNewDraft(
  condominiums: CondominiumOwnershipRow[],
  objectId: string
): CondominiumOwnershipRow | null {
  const persisted = condominiums.filter(c => !c.internalId && c.id && c.objectId === objectId)
  if (persisted.length === 0) {
    return null
  }
  const openCurrent = persisted.find(c =>
    c.status === 'CONDOMINIUM_STATUS_CURRENT' && !isCondominiumEndingAtSet(c.endingAt)
  )
  if (openCurrent) {
    return openCurrent
  }
  return getLatestPersistedCondominiumByStart(persisted, objectId)
}

export function needsShortenPreviousCondominium(
  newDraft: { startingAt?: unknown },
  previous: { startingAt?: unknown; endingAt?: unknown } | null
): boolean {
  if (!previous || !newDraft?.startingAt) {
    return false
  }
  const newStartMs = dateObjectToMs(newDraft.startingAt)
  if (newStartMs === null) {
    return false
  }
  if (isCondominiumEndingAtSet(previous.endingAt)) {
    const previousEndMs = dateObjectToMs(previous.endingAt)
    if (previousEndMs === null) {
      return false
    }
    return newStartMs <= previousEndMs
  }
  const previousStartMs = dateObjectToMs(previous.startingAt)
  if (previousStartMs === null) {
    return false
  }
  return newStartMs > previousStartMs
}

/**
 * True when the new ownership overlaps the previous agreement such that the Attention modal
 * may need to shorten the previous row. Returns false for a bounded historical agreement
 * that ends before the previous contract start (COOZ-15913).
 */
export function newCondominiumRequiresShorteningPrevious(
  newDraft: { startingAt?: unknown; endingAt?: unknown },
  previous: { startingAt?: unknown; endingAt?: unknown } | null
): boolean {
  if (!previous || !newDraft?.startingAt) {
    return false
  }
  const newStartMs = dateObjectToMs(newDraft.startingAt)
  const previousStartMs = dateObjectToMs(previous.startingAt)
  if (newStartMs === null || previousStartMs === null) {
    return false
  }

  const newEndMs = isCondominiumEndingAtSet(newDraft.endingAt) ? dateObjectToMs(newDraft.endingAt) : null
  const previousEndMs = isCondominiumEndingAtSet(previous.endingAt) ? dateObjectToMs(previous.endingAt) : null

  if (newStartMs < previousStartMs) {
    if (newEndMs !== null && newEndMs < previousStartMs) {
      return false
    }
    return true
  }

  if (previousEndMs !== null) {
    return previousEndMs >= newStartMs
  }
  return newStartMs <= previousStartMs
}

/** True when new start is before the previous start but no end date is set (COOZ-15913). */
export function isHistoricalCondominiumMissingEndDate(
  newStart: unknown,
  newEnd: unknown,
  previousStart: unknown
): boolean {
  const newStartMs = dateObjectToMs(newStart)
  const previousStartMs = dateObjectToMs(previousStart)
  if (newStartMs === null || previousStartMs === null || newStartMs >= previousStartMs) {
    return false
  }
  return !isCondominiumEndingAtSet(newEnd)
}

export function isBoundedHistoricalCondominium(
  newDraft: { startingAt?: unknown; endingAt?: unknown },
  previous: { startingAt?: unknown } | null
): boolean {
  if (!previous || !newDraft?.startingAt) {
    return false
  }
  const newStartMs = dateObjectToMs(newDraft.startingAt)
  const previousStartMs = dateObjectToMs(previous.startingAt)
  const newEndMs = isCondominiumEndingAtSet(newDraft.endingAt) ? dateObjectToMs(newDraft.endingAt) : null
  if (newStartMs === null || previousStartMs === null || newEndMs === null) {
    return false
  }
  return newStartMs < previousStartMs && newEndMs < previousStartMs
}

export function startsBeforePreviousCondominium(
  newDraft: { startingAt?: unknown; endingAt?: unknown },
  previous: { startingAt?: unknown; endingAt?: unknown } | null
): boolean {
  return newCondominiumRequiresShorteningPrevious(newDraft, previous)
}

export function hasCondominiumGapAfterPrevious(
  newDraft: { startingAt?: unknown },
  previous: { endingAt?: unknown } | null
): boolean {
  if (!previous || !newDraft?.startingAt || !isCondominiumEndingAtSet(previous.endingAt)) {
    return false
  }
  const newStartMs = dateObjectToMs(newDraft.startingAt)
  const previousEndMs = dateObjectToMs(previous.endingAt)
  if (newStartMs === null || previousEndMs === null) {
    return false
  }
  return newStartMs > previousEndMs + 86400000
}

export function getCondominiumToShorten(
  condominiums: CondominiumOwnershipRow[],
  objectId: string,
  newDraft: { startingAt?: unknown }
): CondominiumOwnershipRow | null {
  const previous = getPreviousCondominiumForNewDraft(condominiums, objectId)
  if (!previous || !needsShortenPreviousCondominium(newDraft, previous)) {
    return null
  }
  return previous
}

export function normalizeCondominiumOwnershipForSave(
  condominiums: CondominiumOwnershipRow[]
) {
  for (const draft of condominiums.filter(c => c.internalId)) {
    if (!draft.objectId || !draft.startingAt) {
      continue
    }
    const previous = getCondominiumToShorten(condominiums, draft.objectId, draft)
    if (!previous) {
      continue
    }
    const endingAt = computePreviousEndingAtFromNewStart(draft.startingAt)
    if (!endingAt) {
      continue
    }
    const index = condominiums.findIndex(c => c.id === previous.id)
    if (index === -1) {
      continue
    }
    condominiums[index].endingAt = endingAt
    condominiums[index].modified = true
  }
}

export function isCondominiumOpenEndedError(message: string | undefined): boolean {
  if (!message) {
    return false
  }
  const lower = message.toLowerCase()
  return lower.includes('open ended') || lower.includes('no endingat set')
}

export function isCondominiumGapError(message: string | undefined): boolean {
  if (!message) {
    return false
  }
  const lower = message.toLowerCase()
  return lower.includes('time gaps') || lower.includes('gaps between condominium')
}
